<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { generateGpx } from '@/api/firebaseAnon';
import { useAlertStore } from '@/store/AlertStore';
import { useStore } from '@/store/Store';
import { distanceString, elevationString } from '@/utils/Utils';
import { injectMap } from '@/utils/MapUtils';
import { storeToRefs } from 'pinia';

const props = defineProps<{
	editor?: boolean;
}>();

const store = useStore();
const alertStore = useAlertStore();
const route = useRoute();
const $map = injectMap();

const { elevationGain, maxAltitude, trackLength, currentRoute, map, uiText } = storeToRefs(store);

const hideGain = computed(() => {
	return currentRoute.value?.properties?.hideGain == true;
});

const hideLoss = computed(() => {
	return currentRoute.value?.properties?.hideLoss !== false;
});

const hideMaxAltitude = computed(() => {
	return currentRoute.value?.properties?.hideMaxAltitude !== false;
});

const gpxLink = computed(() => {
	if (map.value?.id && currentRoute.value?.id) {
		const fileName = `${currentRoute.value.properties?.label ?? 'route'}.gpx`;
		return `/${map.value.id}/${currentRoute.value.id}/gpx/${encodeURIComponent(fileName)}`;
	} else {
		return '';
	}
});

// Public maps follow the GPX link. A saved editor or draft preview must POST
// /api/gpx with the editor token instead. An unsaved map on /create has no download.
const downloadAction = computed(() => {
	if (route.meta?.isNew === true) return "unavailable";
	if (props.editor || route.meta?.draft === true) return "editor-token";
	return "public-link";
});

const showGpx = computed(() => {
	if (downloadAction.value === "unavailable") return false;
	return currentRoute.value?.properties?.downloadGpx !== false && gpxLink.value != "";
});

const gain = computed(() => {
	return elevationString(elevationGain.value.gain, map.value?.unit == "mi" ? "mi" : "km");
});

const loss = computed(() => {
	return elevationString(elevationGain.value.loss, map.value?.unit == "mi" ? "mi" : "km");
});

const maxAltitudeString = computed(() => {
	const altitude = maxAltitude.value;
	if (altitude === undefined) return "";

	const isImperial = map.value?.unit == "mi";
	const altitudeValue = Math.round(isImperial ? altitude * 3.2808399 : altitude);
	const unit = isImperial ? uiText.value.altitudeUnitFt : uiText.value.altitudeUnitM;
	return `${altitudeValue} ${unit}`;
});

const distance = computed(() => {
	return distanceString(trackLength.value, map.value?.unit == "mi" ? "mi" : "km");
});


async function downloadGpx(evt: MouseEvent) {
	// A saved editor or draft preview must POST /api/gpx with the editor token.
	// The public link has no token, so it cannot return an unpublished version.
	if (downloadAction.value === "unavailable") {
		evt.preventDefault();
		return;
	}
	if (downloadAction.value !== "editor-token") return;
	evt.preventDefault();

	try {
		const { success, error, data } = await generateGpx(gpxLink.value);
		if (!success) throw new Error(error);

		const file = new File([data], `${currentRoute.value?.properties?.label}.gpx`, {
			type: 'text/plain',
		});

		//download file hack
		const link = document.createElement('a');
		const url = URL.createObjectURL(file);

		link.href = url;
		link.download = file.name;
		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.log(err);
		alertStore.addMessage("Failed creating GPX file");
	}
}

</script>


<template>
	<div>
		<div class="distance">

			<div>
				<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 24 24">
					<path
						d="M 2 6 C 0.9069372 6 0 6.9069372 0 8 L 0 16 C 0 17.093063 0.9069372 18 2 18 L 22 18 C 23.093063 18 24 17.093063 24 16 L 24 8 C 24 6.9069372 23.093063 6 22 6 L 2 6 z M 2 8 L 3 8 L 3 10 L 5 10 L 5 8 L 7 8 L 7 12 L 9 12 L 9 8 L 11 8 L 11 10 L 13 10 L 13 8 L 15 8 L 15 12 L 17 12 L 17 8 L 19 8 L 19 10 L 21 10 L 21 8 L 22 8 L 22 16 L 2 16 L 2 8 z">
					</path>
				</svg>
				<span>{{ distance }}</span>
			</div>

			<div v-if="!hideGain">
				<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 24 24">
					<path
						d="M 12 2 C 9.44075 2 6.8821875 2.9771875 4.9296875 4.9296875 C 1.0246875 8.8346875 1.0246875 15.165312 4.9296875 19.070312 C 8.8346875 22.975313 15.165313 22.975313 19.070312 19.070312 C 22.975312 15.165312 22.975313 8.8346875 19.070312 4.9296875 C 17.117812 2.9771875 14.55925 2 12 2 z M 12 4 C 14.0475 4 16.09425 4.78175 17.65625 6.34375 C 20.78025 9.46775 20.78025 14.53225 17.65625 17.65625 C 14.53225 20.78025 9.46775 20.78025 6.34375 17.65625 C 3.21975 14.53225 3.21975 9.46775 6.34375 6.34375 C 7.90575 4.78175 9.9525 4 12 4 z M 9 8 L 9 10 L 12.585938 10 L 8 14.585938 L 9.4140625 16 L 14 11.414062 L 14 15 L 16 15 L 16 8 L 9 8 z">
					</path>
				</svg>
				<span>{{ gain }}</span>
			</div>

			<div v-if="!hideLoss">
				<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 24 24">
					<path
						d="M 12 2 C 9.44075 2 6.8821875 2.9771875 4.9296875 4.9296875 C 1.0246875 8.8346875 1.0246875 15.165312 4.9296875 19.070312 C 8.8346875 22.975313 15.165313 22.975313 19.070312 19.070312 C 22.975312 15.165312 22.975313 8.8346875 19.070312 4.9296875 C 17.117812 2.9771875 14.55925 2 12 2 z M 12 4 C 14.0475 4 16.09425 4.78175 17.65625 6.34375 C 20.78025 9.46775 20.78025 14.53225 17.65625 17.65625 C 14.53225 20.78025 9.46775 20.78025 6.34375 17.65625 C 3.21975 14.53225 3.21975 9.46775 6.34375 6.34375 C 7.90575 4.78175 9.9525 4 12 4 z M 9.4140625 8 L 8 9.4140625 L 12.585938 14 L 9 14 L 9 16 L 16 16 L 16 9 L 14 9 L 14 12.585938 L 9.4140625 8 z">
					</path>
				</svg>
				<span>{{ loss }}</span>
			</div>

			<div v-if="!hideMaxAltitude && maxAltitudeString">
				<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 24 24">
					<path
						d="M14 6l-3.5 5L8 8l-6 10h20L14 6z">
					</path>
				</svg>
				<span>{{ maxAltitudeString }}</span>
			</div>

		</div>

		<div class="distance" v-if="showGpx">
			<a class="gpx-button" :href="gpxLink" download @click="downloadGpx">
				<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
					<path fill="currentColor" d="M4 22v-2h16v2zm8-4L5 9h4V2h6v7h4z" />
				</svg>
				GPX
			</a>
			<!-- <button class="gpx-button"">
				Website
			</button> -->
		</div>
	</div>

</template>


<style scoped lang="scss">
.distance {
	padding-left: 0;
	padding-top: .5rem;
	width: 100%;

	color: #777;
	fill: #777;
	font-size: .8rem;

	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
	align-items: center;
	white-space: nowrap;

	div {
		display: flex;
		align-items: center;
		margin-right: 8px;
		margin-bottom: 6px;
	}

	svg {
		width: 1.7em;
		height: 1.7em;
		margin-right: 4px;
		flex: 0 0 auto;
	}

}

.gpx-button {
	font-size: .8rem;
	cursor: pointer;
	border: none;
	margin-right: 0;
	margin-bottom: 6px;
	font-family: unset;
	/*background-color: #444;
	color: white;*/
	background-color: #eaeaea;
	color: #333;
	padding: .4rem .5rem;
	border-radius: 4px;

	display: flex;
	align-items: center;

	margin-right: 8px;

	text-decoration: none;

	svg {
		width: 1.1em;
		height: 1.1em;
		margin-right: 2px;
	}
}
</style>
